'use client'

import type {
  CategoryHeaderContextType,
  CategoryHeaderData,
} from '@maestro/commerce-blocks/providers/category-header/category-header.types'

import { usePathname } from 'next/navigation'
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import { deepMerge } from '@maestro/commerce-blocks/utils/deep-merge.utils'
import { theme } from '@maestro/local/theme'

const CategoryHeaderContext = createContext<
  CategoryHeaderContextType | undefined
>(undefined)

export const CategoryHeaderProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { maxItems } = theme().providers.categoryHeader

  const pathname = usePathname()

  const [selectedCategoryId, setSelectedCategoryIdState] = useState<
    string | number | null
  >(null)

  const [categoryHeaderData, setCategoryHeaderDataState] = useState<
    CategoryHeaderData[]
  >([])
  const [hideProductsCount, setHideProductsCount] = useState(false)

  const setSelectedCategoryId = useCallback((id: string | number) => {
    setSelectedCategoryIdState(id)
  }, [])

  const setCategoryHeaderData = useCallback(
    (data: CategoryHeaderData[], options?: { merge?: boolean }) => {
      setCategoryHeaderDataState((prev) => {
        const map = new Map(prev.map((item) => [item.mainCategoryId, item]))

        data.forEach((item) => {
          const prevItem = map.get(item.mainCategoryId)
          if (options?.merge && prevItem) {
            map.set(item.mainCategoryId, deepMerge(prevItem, item))
          } else {
            map.set(item.mainCategoryId, item)
          }
        })

        const result = Array.from(map.values())
        return result.length > maxItems ? result.slice(-maxItems) : result
      })
    },
    [maxItems]
  )

  // this variable contains data for the currently selected category.
  // Needed in case of navigation to listing pages outside of route to not
  // display category data from previously selected category during loading state
  // until selectedCategoryId state will be updated. Used useMemo to avoid
  // unnecessary re-renders caused by redundant setStates
  const currentCategoryHeaderData = useMemo(() => {
    if (selectedCategoryId == null) {
      return undefined
    }
    const selected = categoryHeaderData.find((item) => {
      return item.mainCategoryId === selectedCategoryId
    })
    if (!selected) {
      return undefined
    }
    if (`/${selected.category.urlPath}` !== pathname) {
      return undefined
    }
    return selected
  }, [selectedCategoryId, categoryHeaderData, pathname])

  return (
    <CategoryHeaderContext.Provider
      value={{
        selectedCategoryId,
        setSelectedCategoryId,
        categoryHeaderData,
        setCategoryHeaderData,
        currentCategoryHeaderData,
        hideProductsCount,
        setHideProductsCount,
      }}
    >
      {children}
    </CategoryHeaderContext.Provider>
  )
}

export const useCategoryHeaderContext = () => {
  const ctx = useContext(CategoryHeaderContext)
  if (!ctx) {
    throw new Error(
      'useCategoryHeaderContext must be used within CategoryHeaderProvider'
    )
  }
  return ctx
}
